import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { HelpCircle } from "lucide-react";

export const FAQ = () => {
  const faqs = [
    {
      question: "How can PACs legally receive energy upgrade benefits?",
      answer: "PACs cannot sign leases or PPAs directly, but our legal framework solves this through the 'PAC-Powered Energy Partnership' model. The district signs all contracts and then enters into a formal MOU with the PAC to allocate 100% of net benefits to the PAC enrichment fund. This structure is fully compliant with educational regulations."
    },
    {
      question: "What are the upfront costs for schools and PACs?",
      answer: "There are zero upfront costs. GreenSmart fully funds all energy installations including solar panels, battery storage, heat pumps, and EV chargers. The district receives immediate rebates and savings, while PACs receive all net financial benefits through the formal agreement structure."
    },
    {
      question: "How much funding can our PAC expect to receive?",
      answer: "Funding varies based on school size, location, and energy usage. Typical installations generate $50,000-$200,000 annually for PAC programs. Use our calculator above for a personalized estimate. After 8 years when equipment is paid off, PACs receive 100% of energy savings indefinitely."
    },
    {
      question: "What types of student programs can be funded?",
      answer: "PACs can fund a wide range of programs including field trips, technology equipment, learning tools, wellness programs, outdoor infrastructure, sports equipment, arts supplies, and emergency preparedness initiatives. All funding must be allocated to student enrichment and educational programs."
    },
    {
      question: "How does the governance and reporting work?",
      answer: "The district provides quarterly financial reports showing all energy savings, rebates, and incentive payments. PACs review and approve all spending in consultation with school principals. All transactions are transparent, audit-ready, and publicly reported to ensure accountability."
    },
    {
      question: "What happens after the 8-year equipment payoff period?",
      answer: "After 8 years, the energy equipment is fully paid off and owned by the district. From that point forward, PACs receive 100% of the energy savings indefinitely - typically for 15+ additional years. This creates a permanent funding stream for student programs."
    },
    {
      question: "How does GreenSmart handle rebates and tax credits?",
      answer: "GreenSmart manages all CleanBC and federal rebate paperwork on behalf of schools. We optimize rebate applications and reinvest credits directly into PAC programs. This includes handling complex compliance requirements and maximizing available incentives."
    },
    {
      question: "What schools are eligible for this program?",
      answer: "We prioritize schools that serve high-density student populations, are located in underserved or emergency-prone areas, and commit to community safety and resilience. Schools of all types (elementary, middle, high school, K-12) can participate if they meet our partnership criteria."
    },
    {
      question: "How long does the implementation process take?",
      answer: "From initial proposal to system activation typically takes 6-12 months. This includes proposal development, district approval, MOU execution, permitting, installation, and system commissioning. PACs begin receiving benefits as soon as systems are operational."
    },
    {
      question: "What if our district is hesitant about the partnership?",
      answer: "We provide comprehensive proposal kits with pre-approved funding menus and co-sign templates for PAC-Principal alignment. Our legal framework is designed to address common district concerns about compliance, liability, and operational requirements. We also offer pilot programs to demonstrate value."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-green-600 border-green-600">
            <HelpCircle className="w-4 h-4 mr-2" />
            Frequently Asked Questions
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Common Questions About the Partnership
          </h2>
          <p className="text-xl text-gray-600">
            Get answers to the most frequently asked questions about the GreenSmart PAC Partnership model.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-600 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Have more questions? We're here to help.
          </p>
          <a 
            href="#contact" 
            className="text-green-600 hover:text-green-700 font-semibold underline"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Contact our team for personalized answers
          </a>
        </div>
      </div>
    </section>
  );
};